import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getAllAssets } from "shared/api/client/orvalHelpers";
import { parseApiError } from "shared/api/client/errorParser";
import {
  OfflineLicense,
  ProvisionResult,
  RegisteredDevice,
  VenuePlaylist,
  addVenuePlaylistItem,
  createVenuePlaylist,
  getRegisteredDevices,
  getVenuePlaylists,
  provisionVenuePlaylist,
  registerDevice,
  removeRegisteredDevice,
  removeVenuePlaylistItem,
} from "../api/device.api";
import { generateRsaKeypair, suggestDeviceId } from "../utils/headsetKeys";
import "./VenueProvisioning.css";

type PublishedTitle = { id: number; title: string };
type Notice = { type: "success" | "error"; text: string } | null;

const PLATFORMS = ["Meta Quest", "Pico", "HTC Vive", "Apple Vision Pro", "Other"];
const DEFAULT_LICENSE_DAYS = 7;
const DEFAULT_APP_VERSION = "1.0.0";

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function downloadJson(data: unknown, fileName: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.setAttribute("hidden", "");
  anchor.setAttribute("href", url);
  anchor.setAttribute("download", fileName);
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  if (typeof window.URL.revokeObjectURL === "function") window.URL.revokeObjectURL(url);
}

function slugify(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "playlist";
}

const LicenseCard: React.FC<{ license: OfflineLicense }> = ({ license }) => {
  const [copied, setCopied] = useState(false);
  const envelope = JSON.stringify(license, null, 2);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(envelope);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="venue-license" data-testid="offline-license">
      <div>
        <strong>{license.deviceId}</strong> · video {license.videoId} · {license.alg} · expires{" "}
        {formatDate(license.expiresUtc)}
      </div>
      <textarea readOnly value={envelope} aria-label={`License for ${license.deviceId}`} />
      <button type="button" className="venue-btn secondary small" onClick={copy}>
        {copied ? "Copied" : "Copy license"}
      </button>
    </div>
  );
};

const VenueProvisioning: React.FC = () => {
  const [notice, setNotice] = useState<Notice>(null);

  const [devices, setDevices] = useState<RegisteredDevice[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [deviceForm, setDeviceForm] = useState({
    deviceId: "",
    platform: PLATFORMS[0],
    publicKey: "",
    appVersion: "",
  });
  const [registering, setRegistering] = useState(false);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [generatedPrivateKey, setGeneratedPrivateKey] = useState<string | null>(null);

  const [playlists, setPlaylists] = useState<VenuePlaylist[]>([]);
  const [playlistsLoading, setPlaylistsLoading] = useState(true);
  const [published, setPublished] = useState<PublishedTitle[]>([]);
  const [playlistForm, setPlaylistForm] = useState({
    name: "",
    venueName: "",
    screeningDate: "",
  });
  const [selectedAssetIds, setSelectedAssetIds] = useState<number[]>([]);
  const [creating, setCreating] = useState(false);

  const [addAssetByPlaylist, setAddAssetByPlaylist] = useState<
    Record<number, string>
  >({});
  const [daysByPlaylist, setDaysByPlaylist] = useState<Record<number, string>>(
    {}
  );
  const [provisioningId, setProvisioningId] = useState<number | null>(null);
  const [provisioned, setProvisioned] = useState<
    Record<number, ProvisionResult>
  >({});

  const fail = useCallback((err: unknown, fallback: string) => {
    setNotice({ type: "error", text: parseApiError(err, fallback) });
  }, []);

  const loadDevices = useCallback(async () => {
    try {
      setDevices(await getRegisteredDevices());
    } catch (err) {
      fail(err, "Could not load registered headsets.");
    } finally {
      setDevicesLoading(false);
    }
  }, [fail]);

  const loadPlaylists = useCallback(async () => {
    try {
      setPlaylists(await getVenuePlaylists());
    } catch (err) {
      fail(err, "Could not load venue playlists.");
    } finally {
      setPlaylistsLoading(false);
    }
  }, [fail]);

  useEffect(() => {
    loadDevices();
    loadPlaylists();
    getAllAssets()
      .then((assets) =>
        setPublished(
          assets
            .filter((a: any) => a?.status === "Published")
            .map((a: any) => ({
              id: Number(a.id),
              title: String(a.title || `Title #${a.id}`),
            }))
        )
      )
      .catch((err) => fail(err, "Could not load published titles."));
  }, [loadDevices, loadPlaylists, fail]);

  const titleById = useMemo(
    () => new Map(published.map((p) => [p.id, p.title])),
    [published]
  );

  const handleGenerateKeypair = async () => {
    setGeneratingKey(true);
    try {
      const keypair = await generateRsaKeypair();
      setDeviceForm((p) => ({
        ...p,
        deviceId: p.deviceId.trim() || suggestDeviceId(),
        appVersion: p.appVersion.trim() || DEFAULT_APP_VERSION,
        publicKey: keypair.publicKeyPem,
      }));
      setGeneratedPrivateKey(keypair.privateKeyPkcs8Base64);
      setNotice({
        type: "success",
        text: "RSA-2048 keypair generated. Keep the private key with the headset — only the public key is registered.",
      });
    } catch (err) {
      fail(err, "Could not generate a keypair in this browser.");
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      deviceId: deviceForm.deviceId.trim(),
      platform: deviceForm.platform,
      publicKey: deviceForm.publicKey.trim(),
      appVersion: deviceForm.appVersion.trim(),
    };
    if (!payload.deviceId || !payload.publicKey || !payload.appVersion) {
      setNotice({
        type: "error",
        text: "Device ID, app version and public key are required.",
      });
      return;
    }
    setRegistering(true);
    try {
      await registerDevice(payload);
      setDeviceForm({ deviceId: "", platform: PLATFORMS[0], publicKey: "", appVersion: "" });
      setGeneratedPrivateKey(null);
      setNotice({ type: "success", text: `Headset ${payload.deviceId} registered.` });
      await loadDevices();
    } catch (err) {
      fail(err, "Could not register the headset.");
    } finally {
      setRegistering(false);
    }
  };

  const handleRemoveDevice = async (deviceId: string) => {
    try {
      await removeRegisteredDevice(deviceId);
      setDevices((prev) => prev.filter((d) => d.deviceId !== deviceId));
      setNotice({ type: "success", text: `Headset ${deviceId} removed.` });
    } catch (err) {
      fail(err, "Could not remove the headset.");
    }
  };

  const toggleAsset = (id: number) =>
    setSelectedAssetIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const handleCreatePlaylist = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = playlistForm.name.trim();
    const venueName = playlistForm.venueName.trim();
    if (!name || !venueName) {
      setNotice({ type: "error", text: "Playlist name and venue name are required." });
      return;
    }
    setCreating(true);
    try {
      await createVenuePlaylist({
        name,
        venueName,
        screeningDate: playlistForm.screeningDate || undefined,
        assetIds: selectedAssetIds,
      });
      setPlaylistForm({ name: "", venueName: "", screeningDate: "" });
      setSelectedAssetIds([]);
      setNotice({ type: "success", text: `Playlist "${name}" created.` });
      await loadPlaylists();
    } catch (err) {
      fail(err, "Could not create the playlist.");
    } finally {
      setCreating(false);
    }
  };

  const handleAddItem = async (playlistId: number) => {
    const assetId = Number(addAssetByPlaylist[playlistId]);
    if (!assetId) return;
    try {
      await addVenuePlaylistItem(playlistId, assetId);
      setAddAssetByPlaylist((prev) => ({ ...prev, [playlistId]: "" }));
      await loadPlaylists();
    } catch (err) {
      fail(err, "Could not add the title to the playlist.");
    }
  };

  const handleRemoveItem = async (playlistId: number, itemId: number) => {
    try {
      await removeVenuePlaylistItem(playlistId, itemId);
      await loadPlaylists();
    } catch (err) {
      fail(err, "Could not remove the title from the playlist.");
    }
  };

  const handleProvision = async (playlist: VenuePlaylist) => {
    const raw = daysByPlaylist[playlist.id];
    const days = raw ? Number(raw) : DEFAULT_LICENSE_DAYS;
    if (!Number.isInteger(days) || days < 1 || days > 365) {
      setNotice({ type: "error", text: "License validity must be 1–365 days." });
      return;
    }
    setProvisioningId(playlist.id);
    try {
      const result = await provisionVenuePlaylist(playlist.id, days);
      setProvisioned((prev) => ({ ...prev, [playlist.id]: result }));
      setNotice({
        type: result.failures.length ? "error" : "success",
        text: `Minted ${result.licenses.length} offline license(s) for "${playlist.name}".${result.failures.length ? ` Failures: ${result.failures.join("; ")}` : ""}`,
      });
      await loadPlaylists();
    } catch (err) {
      fail(err, "Could not provision offline licenses.");
    } finally {
      setProvisioningId(null);
    }
  };

  const handleExportBundle = (playlist: VenuePlaylist, result: ProvisionResult) => {
    const bundle = {
      bundleVersion: 1,
      exportedAt: new Date().toISOString(),
      playlist: {
        id: playlist.id,
        name: playlist.name,
        venueName: playlist.venueName,
        screeningDate: playlist.screeningDate,
        items: playlist.items,
      },
      devices: devices.map((d) => ({ deviceId: d.deviceId, platform: d.platform })),
      provisioning: {
        expiresUtc: result.expiresUtc,
        deviceCount: result.deviceCount,
        videoCount: result.videoCount,
        failures: result.failures,
      },
      licenses: result.licenses,
    };
    downloadJson(bundle, `poetics-stream-provisioning-${slugify(playlist.name)}-${playlist.id}.json`);
    setNotice({ type: "success", text: `Provisioning bundle for "${playlist.name}" exported.` });
  };

  return (
    <section className="venue-dashboard" aria-labelledby="venue-dashboard-title">
      <h2 id="venue-dashboard-title">Venue Screening &amp; Headset Provisioning</h2>
      <p className="venue-subtitle">
        Register headsets and mint offline screening licenses for venue playlists.
      </p>

      {notice && (
        <div className={`venue-message ${notice.type}`} role={notice.type === "error" ? "alert" : "status"}>
          {notice.text}
        </div>
      )}

      <div className="venue-panel">
        <h3>Registered Headsets</h3>
        {devicesLoading ? (
          <p className="venue-empty">Loading headsets…</p>
        ) : devices.length === 0 ? (
          <p className="venue-empty">No headsets registered yet.</p>
        ) : (
          <table className="venue-table">
            <thead>
              <tr>
                <th>Device ID</th>
                <th>Platform</th>
                <th>Registered</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => (
                <tr key={d.deviceId}>
                  <td>{d.deviceId}</td>
                  <td>{d.platform}</td>
                  <td>{formatDate(d.registeredAt)}</td>
                  <td>
                    <button
                      type="button"
                      className="venue-btn danger small"
                      onClick={() => handleRemoveDevice(d.deviceId)}
                      aria-label={`Remove ${d.deviceId}`}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <form onSubmit={handleRegister} style={{ marginTop: 20 }}>
          <h3>Register Headset Public Key</h3>
          <div className="venue-row">
            <label className="venue-field">
              Device ID
              <input
                value={deviceForm.deviceId}
                onChange={(e) => setDeviceForm((p) => ({ ...p, deviceId: e.target.value }))}
                placeholder="e.g. QUEST3-A1B2C3"
              />
            </label>
            <label className="venue-field">
              Platform
              <select
                value={deviceForm.platform}
                onChange={(e) => setDeviceForm((p) => ({ ...p, platform: e.target.value }))}
              >
                {PLATFORMS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="venue-row">
            <label className="venue-field">
              App version
              <input
                value={deviceForm.appVersion}
                onChange={(e) => setDeviceForm((p) => ({ ...p, appVersion: e.target.value }))}
                placeholder="e.g. 1.0.0"
              />
            </label>
            <label className="venue-field">
              Public key (PEM or base64)
              <textarea
                value={deviceForm.publicKey}
                onChange={(e) => setDeviceForm((p) => ({ ...p, publicKey: e.target.value }))}
                placeholder="-----BEGIN PUBLIC KEY-----"
              />
            </label>
          </div>
          {generatedPrivateKey && (
            <details className="venue-private-key">
              <summary>Generated private key (PKCS#8, base64) — store it on the headset, it is never uploaded</summary>
              <textarea readOnly value={generatedPrivateKey} aria-label="Generated private key" />
            </details>
          )}
          <div className="venue-row" style={{ marginBottom: 0 }}>
            <button type="submit" className="venue-btn" disabled={registering}>
              {registering ? "Registering…" : "Register Headset"}
            </button>
            <button
              type="button"
              className="venue-btn secondary"
              onClick={handleGenerateKeypair}
              disabled={generatingKey || registering}
            >
              {generatingKey ? "Generating…" : "Generate RSA-2048 Keypair"}
            </button>
          </div>
        </form>
      </div>

      <div className="venue-panel">
        <h3>Venue Screening Playlists</h3>

        <form onSubmit={handleCreatePlaylist}>
          <div className="venue-row">
            <label className="venue-field">
              Playlist Name
              <input
                value={playlistForm.name}
                onChange={(e) => setPlaylistForm((p) => ({ ...p, name: e.target.value }))}
              />
            </label>
            <label className="venue-field">
              Venue Name
              <input
                value={playlistForm.venueName}
                onChange={(e) => setPlaylistForm((p) => ({ ...p, venueName: e.target.value }))}
              />
            </label>
            <label className="venue-field">
              Screening Date
              <input
                type="date"
                value={playlistForm.screeningDate}
                onChange={(e) => setPlaylistForm((p) => ({ ...p, screeningDate: e.target.value }))}
              />
            </label>
          </div>
          <div className="venue-field" style={{ marginBottom: 12 }}>
            Published titles
            {published.length === 0 ? (
              <span className="venue-empty">No published titles available.</span>
            ) : (
              <div className="venue-asset-picker">
                {published.map((p) => (
                  <label key={p.id}>
                    <input
                      type="checkbox"
                      checked={selectedAssetIds.includes(p.id)}
                      onChange={() => toggleAsset(p.id)}
                    />
                    {p.title}
                  </label>
                ))}
              </div>
            )}
          </div>
          <button type="submit" className="venue-btn" disabled={creating}>
            {creating ? "Creating…" : "Create Playlist"}
          </button>
        </form>

        <div style={{ marginTop: 24 }}>
          {playlistsLoading ? (
            <p className="venue-empty">Loading playlists…</p>
          ) : playlists.length === 0 ? (
            <p className="venue-empty">No venue playlists yet.</p>
          ) : (
            playlists.map((pl) => {
              const result = provisioned[pl.id];
              const available = published.filter(
                (p) => !pl.items.some((i) => i.assetId === p.id)
              );
              return (
                <div className="venue-playlist" key={pl.id} data-testid="venue-playlist">
                  <strong>{pl.name}</strong>
                  <div className="venue-playlist-meta">
                    {pl.venueName}
                    {pl.screeningDate ? ` · ${pl.screeningDate}` : ""}
                    {` · ${pl.activeLicenseCount} active / ${pl.licenseCount} total licenses`}
                  </div>

                  {pl.items.length === 0 ? (
                    <p className="venue-empty">No titles in this playlist.</p>
                  ) : (
                    <table className="venue-table">
                      <tbody>
                        {pl.items.map((item) => (
                          <tr key={item.id}>
                            <td>{item.title || titleById.get(item.assetId) || `#${item.assetId}`}</td>
                            <td style={{ textAlign: "right" }}>
                              <button
                                type="button"
                                className="venue-btn secondary small"
                                onClick={() => handleRemoveItem(pl.id, item.id)}
                                aria-label={`Remove ${item.title} from ${pl.name}`}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  <div className="venue-row" style={{ marginTop: 12 }}>
                    <label className="venue-field">
                      Add title
                      <select
                        value={addAssetByPlaylist[pl.id] ?? ""}
                        onChange={(e) =>
                          setAddAssetByPlaylist((prev) => ({ ...prev, [pl.id]: e.target.value }))
                        }
                      >
                        <option value="">Select a published title</option>
                        {available.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.title}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      className="venue-btn secondary"
                      onClick={() => handleAddItem(pl.id)}
                      disabled={!addAssetByPlaylist[pl.id]}
                    >
                      Add
                    </button>
                    <label className="venue-field" style={{ flex: "0 0 120px" }}>
                      Valid for (days)
                      <input
                        type="number"
                        min={1}
                        max={365}
                        value={daysByPlaylist[pl.id] ?? String(DEFAULT_LICENSE_DAYS)}
                        onChange={(e) =>
                          setDaysByPlaylist((prev) => ({ ...prev, [pl.id]: e.target.value }))
                        }
                      />
                    </label>
                    <button
                      type="button"
                      className="venue-btn"
                      onClick={() => handleProvision(pl)}
                      disabled={provisioningId === pl.id}
                    >
                      {provisioningId === pl.id ? "Provisioning…" : "Provision Offline Licenses"}
                    </button>
                  </div>

                  {result && (
                    <div>
                      <div className="venue-row" style={{ marginTop: 12, alignItems: "center" }}>
                        <div className="venue-playlist-meta" style={{ flex: "1 1 auto" }}>
                          {result.licenses.length} license(s) issued, valid until{" "}
                          {formatDate(result.expiresUtc)}
                        </div>
                        <button
                          type="button"
                          className="venue-btn secondary small"
                          onClick={() => handleExportBundle(pl, result)}
                          disabled={result.licenses.length === 0}
                        >
                          Export Provisioning Bundle
                        </button>
                      </div>
                      {result.licenses.map((lic) => (
                        <LicenseCard key={lic.licId} license={lic} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
};

export default VenueProvisioning;
